import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { hasAccess } from "@/lib/permissions";
import InstallAppButton from "@/components/InstallAppButton";
import {
  Briefcase, FileText, Users, Truck, FolderOpen,
  BarChart3, Settings as SettingsIcon, Bell, ChevronDown, LogOut,
  Recycle, Car, Receipt, StickyNote, ArrowUpRight, AlertTriangle, IdCard, ScrollText, ShieldCheck, ListChecks,
} from "lucide-react";

const TILES = [
  // Operations
  { id: "jobs",      perm: "jobs",       group: "Operations",        accent: "amber",   title: "Jobs",                          to: "/jobs",                    icon: Briefcase,    testid: "tile-jobs",        hint: "Track every delivery job" },
  { id: "clients",   perm: "clients",    group: "Operations",        accent: "amber",   title: "Clients",                       to: "/clients",                 icon: Users,        testid: "tile-clients",     hint: "Clients & their sites" },
  { id: "subs",      perm: "suppliers",  group: "Operations",        accent: "amber",   title: "Suppliers",                     to: "/contacts",                icon: Truck,        testid: "tile-suppliers",   hint: "Subcontractors & self-employed drivers" },

  // Finance & Billing
  { id: "pos-sbs",   perm: "pos-sbs",    group: "Finance & Billing", accent: "emerald", title: "Purchase Orders & Self-Bills",  to: "/purchase-orders",         icon: FileText,     testid: "tile-pos-sbs",     hint: "Issue POs and self-bills" },
  { id: "sub-inv",   perm: "sub-inv",    group: "Finance & Billing", accent: "emerald", title: "Subcontractor Invoices",        to: "/subcontractor-invoices",  icon: Receipt,      testid: "tile-sub-invoices",hint: "Match PO ↔ supplier invoice" },
  { id: "finance",   perm: "finance",    group: "Finance & Billing", accent: "emerald", title: "Finance",                       to: "/finance/chargeable",      icon: FolderOpen,   testid: "tile-finance",     hint: "Chargeable & Payable" },

  // Compliance & Fleet
  { id: "vehicles",  perm: "vehicles",   group: "Compliance & Fleet",accent: "sky",     title: "D4PS Vehicles",                 to: "/vehicles",                icon: Car,          testid: "tile-vehicles",    hint: "Fleet, MOT, tax & insurance" },
  { id: "d4ps-docs", perm: "d4ps-docs",  group: "Company Compliance",accent: "teal",     title: "D4PS Certifications",           to: "/d4ps-documents",          icon: FolderOpen,   testid: "tile-d4ps-docs",   hint: "VAT, ISO, FORS & certificates" },
  { id: "d4ps-insurance", perm: "d4ps-insurance", group: "Company Compliance", accent: "teal", title: "D4PS Insurances",   to: "/d4ps-insurances",         icon: ShieldCheck,  testid: "tile-d4ps-insurance", hint: "Insurance policies & renewals" },
  { id: "waste",     perm: "waste",      group: "Compliance & Fleet",accent: "sky",     title: "Waste Transfer Notes",          to: "/waste-notes",             icon: Recycle,      testid: "tile-waste",       hint: "Section 34 EPA compliance" },
  { id: "hr",        perm: "hr",         group: "Compliance & Fleet",accent: "sky",     title: "HR",                            to: "/hr",                      icon: IdCard,       testid: "tile-hr",          hint: "Driver documents & licence checks" },
  { id: "policies",  perm: "policies",   group: "Company Compliance",accent: "teal",     title: "Policies",                      to: "/policies",                icon: ScrollText,   testid: "tile-policies",    hint: "Company policies & review dates" },
  { id: "procedures", perm: "procedures", group: "Compliance & Fleet",accent: "sky",     title: "Procedures",                    to: "/procedures",              icon: ListChecks,   testid: "tile-procedures",  hint: "Operating procedures & review dates" },

  // Admin
  { id: "reports",   perm: "reports",    group: "Admin",             accent: "rose",    title: "Reports",                       to: "/reports",                 icon: BarChart3,    testid: "tile-reports",     hint: "Business analytics" },
  { id: "notes",     perm: "notes",      group: "Admin",             accent: "rose",    title: "Notes",                         to: "/notes",                   icon: StickyNote,   testid: "tile-notes",       hint: "Reminders & jottings" },
  { id: "settings",  perm: "settings",   group: "Admin",             accent: "rose",    title: "Settings",                      to: "/settings",                icon: SettingsIcon, testid: "tile-settings",    hint: "Company & users" },
];

const GROUP_ORDER = ["Operations", "Finance & Billing", "Compliance & Fleet", "Company Compliance", "Admin"];

export default function Hub() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState({ expiring: [], notes: [], total: 0 });
  const menuRef = useRef(null);
  const notifRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    api.get("/notifications").then((r) => {
      const data = r.data || {};
      setNotifs({
        expiring: data.expiring || [],
        notes:    data.assigned_notes || [],
        total:    Number(data.total || 0),
      });
    }).catch(() => setNotifs({ expiring: [], notes: [], total: 0 }));
  }, []);

  const markNoteSeen = async (noteId) => {
    try { await api.post(`/notes/${noteId}/seen`); } catch (_) { /* ignore */ }
    setNotifs((curr) => ({
      ...curr,
      notes: curr.notes.filter((n) => n.id !== noteId),
      total: Math.max(0, curr.total - 1),
    }));
  };

  const initials = (user?.name || user?.email || "U")
    .split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  const handleLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  const today = new Date();
  const dateLabel = today.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const firstName = (user?.name || "").split(" ")[0] || "team";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="hub-page">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center gap-3 sm:gap-4">
          <Link to="/" className="flex items-center gap-2.5 sm:gap-3.5 group min-w-0" data-testid="hub-brand">
            <img src="/d4ps-logo.png" alt="D4PS" className="h-9 w-9 sm:h-12 sm:w-12 object-contain shrink-0" />
            <div className="leading-none min-w-0">
              <div className="flex items-baseline gap-1.5">
                <span className="font-display font-extrabold text-xl sm:text-3xl tracking-tight text-slate-900 group-hover:text-slate-800 transition-colors">D4PS</span>
                <span className="inline-block h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-orange-500" />
              </div>
              <div className="mt-1 sm:mt-1.5 text-[8.5px] sm:text-[10px] tracking-[0.22em] sm:tracking-[0.28em] text-slate-500 font-semibold truncate">COURIERS &amp; LOGISTICS</div>
            </div>
          </Link>

          <div className="ml-auto flex items-center gap-2">
            <InstallAppButton />
            {/* Notifications */}
            <div className="relative" ref={notifRef}>
              <button
                data-testid="hub-notifications"
                onClick={() => setNotifOpen((v) => !v)}
                className="relative h-10 w-10 rounded-sm hover:bg-slate-100 flex items-center justify-center text-slate-600"
                title="Notifications"
              >
                <Bell className="h-5 w-5" />
                {notifs.total > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold inline-flex items-center justify-center" data-testid="hub-notif-count">
                    {notifs.total > 9 ? "9+" : notifs.total}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div data-testid="hub-notif-panel" className="absolute right-0 mt-1 w-96 bg-white border border-slate-200 rounded-sm shadow-xl z-30 overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                    <div className="text-sm font-medium text-slate-900">Your attention</div>
                    <div className="ml-auto text-[11px] text-slate-500">{notifs.total} item{notifs.total === 1 ? "" : "s"}</div>
                  </div>
                  {notifs.total === 0 ? (
                    <div className="px-4 py-8 text-center text-xs text-slate-500">
                      All clear — no notes or expiring documents need you right now.
                    </div>
                  ) : (
                    <div className="max-h-[28rem] overflow-y-auto">
                      {notifs.notes.length > 0 && (
                        <div>
                          <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Notes for you</div>
                          <ul className="divide-y divide-slate-100">
                            {notifs.notes.map((n) => (
                              <li key={n.id} data-testid={`hub-notif-note-${n.id}`}>
                                <Link
                                  to="/notes"
                                  onClick={() => { markNoteSeen(n.id); setNotifOpen(false); }}
                                  className="block px-4 py-3 hover:bg-slate-50"
                                >
                                  <div className="flex items-start gap-2.5">
                                    <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-orange-500 shrink-0" />
                                    <div className="min-w-0 flex-1">
                                      <div className="text-sm font-medium text-slate-900 truncate">{n.title}</div>
                                      {n.body_preview && <div className="text-[12px] text-slate-600 mt-0.5 line-clamp-2">{n.body_preview}</div>}
                                      <div className="text-[11px] text-slate-500 mt-1">From {n.from_name || n.from_email || "—"}</div>
                                    </div>
                                  </div>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {notifs.expiring.length > 0 && (
                        <div>
                          <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Expiring documents</div>
                          <ul className="divide-y divide-slate-100">
                            {notifs.expiring.map((n) => {
                              const isRed = n.tone === "red";
                              const label = isRed
                                ? `Expired ${Math.abs(n.days)}d ago`
                                : n.days === 0 ? "Expires today" : `Expires in ${n.days}d`;
                              return (
                                <li key={n.id} data-testid={`hub-notif-${n.id}`}>
                                  <Link
                                    to={n.link || "/d4ps-documents"}
                                    onClick={() => setNotifOpen(false)}
                                    className="block px-4 py-3 hover:bg-slate-50"
                                  >
                                    <div className="flex items-start gap-2.5">
                                      <span className={`mt-1.5 inline-block h-1.5 w-1.5 rounded-full ${isRed ? "bg-red-500" : "bg-orange-500"} shrink-0`} />
                                      <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-slate-900 truncate">{n.filename}</div>
                                        <div className="text-[11px] text-slate-500 mt-0.5">{n.category_name}</div>
                                      </div>
                                      <div className={`text-[11px] font-semibold tabular-nums shrink-0 ${isRed ? "text-red-600" : "text-orange-600"}`}>{label}</div>
                                    </div>
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-right">
                    <Link to="/notes" onClick={() => setNotifOpen(false)} className="text-xs font-medium text-slate-700 hover:text-slate-900 mr-3">
                      Open notes →
                    </Link>
                    <Link to="/d4ps-documents" onClick={() => setNotifOpen(false)} className="text-xs font-medium text-slate-700 hover:text-slate-900">
                      Documents →
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Profile menu */}
            <div className="relative" ref={menuRef}>
              <button
                data-testid="hub-profile-btn"
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 h-10 px-2 rounded-sm hover:bg-slate-100"
              >
                <div className="h-8 w-8 rounded-sm bg-slate-900 text-white flex items-center justify-center text-sm font-semibold">
                  {initials}
                </div>
                <ChevronDown className="h-4 w-4 text-slate-500" />
              </button>
              {menuOpen && (
                <div data-testid="hub-profile-menu" className="absolute right-0 mt-1 w-64 bg-white border border-slate-200 rounded-sm shadow-lg z-30">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <div className="text-sm font-medium text-slate-900 truncate">{user?.name}</div>
                    <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                    <div className="mt-2 inline-block text-[10px] uppercase tracking-[0.18em] bg-slate-100 text-slate-700 px-2 py-0.5 rounded-sm">
                      {user?.role || "user"}
                    </div>
                  </div>
                  <Link to="/settings" className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={() => setMenuOpen(false)}>
                    Settings
                  </Link>
                  <button
                    data-testid="hub-logout"
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-slate-50 inline-flex items-center gap-2"
                  >
                    <LogOut className="h-4 w-4" /> Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero band */}
      <section className="relative overflow-hidden bg-slate-950 text-white border-b border-slate-900">
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            background:
              "radial-gradient(50% 80% at 90% 0%, rgba(249,115,22,0.55), transparent 60%), radial-gradient(40% 60% at 0% 100%, rgba(255,255,255,0.14), transparent 60%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage: "radial-gradient(70% 100% at 50% 0%, black 60%, transparent 100%)",
            WebkitMaskImage: "radial-gradient(70% 100% at 50% 0%, black 60%, transparent 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
            <div>
              <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.28em] sm:tracking-[0.32em] text-orange-400/95 font-semibold inline-flex items-center gap-2">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-orange-500" />
                Operations dashboard
              </div>
              <h1 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl tracking-tight font-semibold leading-[1.05]">
                Welcome back, <span className="text-orange-400">{firstName}</span>.
              </h1>
              <div className="mt-3 h-[3px] w-12 bg-orange-500" />
              <p className="mt-4 text-slate-300 text-sm md:text-[15px] max-w-xl">
                The D4PS control room — every job, vehicle, invoice and document, organised and ready to go.
              </p>
            </div>
            <div className="md:text-right">
              <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-slate-400 font-semibold">Today</div>
              <div className="mt-1 font-display text-base md:text-lg text-white tabular-nums">{dateLabel}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 space-y-14">
        {GROUP_ORDER.map((group) => {
          const groupTiles = TILES.filter((t) => t.group === group && hasAccess(user, t.perm) && !(t.id === "waste" && hasAccess(user, "jobs")));
          if (groupTiles.length === 0) return null;
          return (
            <section key={group} data-testid={`hub-group-${group.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="flex items-end justify-between mb-5">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.28em] text-orange-600 font-semibold">{group}</div>
                  <div className="mt-1 h-px w-10 bg-slate-300" />
                </div>
                <div className="text-[11px] text-slate-400 tabular-nums">{groupTiles.length} sections</div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                {groupTiles.map((t) => (
                  <Tile key={t.id} tile={t} />
                ))}
              </div>
            </section>
          );
        })}
      </main>
    </div>
  );
}

const ACCENTS = {
  amber:   { stripe: "bg-orange-500",  ring: "ring-orange-500/30",  glow: "shadow-[0_8px_24px_-12px_rgba(249,115,22,0.6)]",    hoverArrow: "group-hover:text-orange-500",  chip: "bg-orange-500" },
  emerald: { stripe: "bg-emerald-400", ring: "ring-emerald-400/30", glow: "shadow-[0_8px_24px_-12px_rgba(16,185,129,0.55)]",   hoverArrow: "group-hover:text-emerald-500", chip: "bg-emerald-400" },
  teal:    { stripe: "bg-teal-400",    ring: "ring-teal-400/30",    glow: "shadow-[0_8px_24px_-12px_rgba(45,212,191,0.55)]",   hoverArrow: "group-hover:text-teal-500",    chip: "bg-teal-400" },
  sky:     { stripe: "bg-sky-400",     ring: "ring-sky-400/30",     glow: "shadow-[0_8px_24px_-12px_rgba(56,189,248,0.55)]",   hoverArrow: "group-hover:text-sky-500",     chip: "bg-sky-400" },
  rose:    { stripe: "bg-rose-400",    ring: "ring-rose-400/30",    glow: "shadow-[0_8px_24px_-12px_rgba(244,114,182,0.55)]",  hoverArrow: "group-hover:text-rose-500",    chip: "bg-rose-400" },
};

function Tile({ tile }) {
  const Icon = tile.icon;
  const a = ACCENTS[tile.accent] || ACCENTS.amber;
  return (
    <Link
      to={tile.to}
      data-testid={tile.testid}
      className="group relative bg-white border border-slate-200 rounded-sm p-6 flex flex-col gap-5 min-h-[180px] overflow-hidden hover:border-slate-900 hover:shadow-[0_12px_28px_-14px_rgba(15,23,42,0.25)] transition-all"
    >
      {/* Top accent — hidden until hover */}
      <span className={`absolute left-0 right-0 top-0 h-[3px] ${a.stripe} opacity-0 group-hover:opacity-100 transition-opacity`} />
      {/* Soft accent glow behind icon */}
      <span className={`absolute -top-10 -left-10 h-32 w-32 rounded-full ${a.stripe} opacity-0 group-hover:opacity-[0.18] blur-2xl transition-opacity pointer-events-none`} />

      <div className="flex items-start justify-between relative">
        <div className={`relative h-14 w-14 rounded-md flex items-center justify-center text-white bg-gradient-to-br from-slate-900 to-slate-800 ring-1 ${a.ring} ${a.glow} group-hover:scale-[1.06] group-hover:-rotate-3 transition-transform duration-300`}>
          {/* Inner highlight */}
          <span className="absolute inset-0 rounded-md bg-gradient-to-b from-white/10 to-transparent pointer-events-none" />
          {/* Accent chip in corner */}
          <span className={`absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full ${a.chip} ring-2 ring-white`} />
          <Icon className="h-6 w-6 relative z-10" />
        </div>
        <ArrowUpRight className={`h-4 w-4 text-slate-300 group-hover:${a.text} group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all`} />
      </div>
      <div className="mt-auto relative">
        <div className="font-display text-[17px] font-semibold text-slate-900 leading-snug">{tile.title}</div>
        <div className="mt-1.5 text-[12.5px] text-slate-500 leading-snug">{tile.hint}</div>
      </div>
    </Link>
  );
}
